import React, { FC, ReactNode } from 'react';
import { Tooltip, ClickAwayListener, makeStyles } from '@material-ui/core';
import { BoxV2 as Box, SxProps } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';

const useStyles = makeStyles({
  customTooltip: {
    margin: 0,
    padding: 0,
    maxWidth: 526,
    color: '#333E41',
    backgroundColor: '#fff',
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: '14px',
    borderRadius: '3px',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
  },
});

interface Props {
  data: string[];
  interactive?: boolean;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
  footer?: ReactNode;
  popperSx?: SxProps;
  sx?: SxProps;
}

const VettingScoreTooltip: FC<Props> = ({
  data,
  placement = 'bottom-end',
  interactive,
  footer,
  popperSx,
  sx,
}) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        PopperProps={{
          disablePortal: true,
          popperOptions: {
            positionFixed: true,
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'window',
              },
            },
          },
        }}
        id="vettingScoreTooltip"
        open={open}
        interactive={interactive}
        onClose={() => setOpen(false)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        placement={placement}
        classes={{
          tooltip: classes.customTooltip,
        }}
        title={
          <Box sx={{ color: 't.black80', fontSize: 'H300', width: '526px' }}>
            <Box
              sx={{
                fontSize: 'H400',
                fontWeight: 600,
                backgroundColor: 't.blue100',
                color: 't.white',
                px: 'l',
                py: 's',
              }}
            >
              Please see the vetting details below:
            </Box>
            {data.length > 0 && (
              <Box sx={{ p: 'm', pt: 's' }}>
                <ul style={{ paddingLeft: 14 }} data-testid="vettingDetailList">
                  {data.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </Box>
            )}
            {footer && (
              <Box sx={{ px: 'm' }}>
                {data.length > 0 && (
                  <Box
                    as="hr"
                    sx={{
                      height: '2px',
                      backgroundColor: '#68737A',
                      px: 'l',
                    }}
                  />
                )}
                {footer}
              </Box>
            )}
          </Box>
        }
      >
        <Box
          onClick={() => setOpen(!open)}
          sx={{
            backgroundColor: open ? 't.paleBlue400' : 'transparent',
            cursor: open ? 'pointer' : 'default',
            borderRadius: open ? '50%' : '0%',
            ...sx,
            '&:hover': {
              backgroundColor: 't.paleBlue400',
              cursor: 'pointer',
              borderRadius: '50%',
            },
            '+ .MuiTooltip-popper': {
              left: '100px !important',
              ...popperSx,
            },
          }}
          data-testid="vettingScoreTooltipIcon"
        >
          <FontAwesomeIcon icon={faEye} fontSize={12} />
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default VettingScoreTooltip;
